import { Component, effect, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import dayjs from 'dayjs';
import { LpDkDokumenterDatasource } from '@features/letpensiondk/letpensiondk-dokumenter/dokumenter.datasource';
import { InputText } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { DokumenterService } from '@features/letpensiondk/letpensiondk-dokumenter/dokumenter.service';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { FileSelectEvent, FileUploadModule } from 'primeng/fileupload';
import { appConfig } from '@core/app-config';

@Component({
  selector: 'app-letpensiondk-dokumenter-detail',
  imports: [
    ReactiveFormsModule,
    InputText,
    ButtonModule,
    MessageModule,
    ConfirmDialog,
    FileUploadModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './letpensiondk-dokumenter-detail.component.html',
  styleUrl: './letpensiondk-dokumenter-detail.component.css',
})
export class LetpensiondkDokumenterDetailComponent {
  fb = inject(FormBuilder);
  confirmationService = inject(ConfirmationService);
  dokumenterService = inject(DokumenterService);
  datasource = inject(LpDkDokumenterDatasource);
  form: FormGroup;

  saving = signal(false);

  constructor() {
    this.form = this.fb.group({
      emne: ['', { validators: [Validators.required] }],
      indhold: [],
      oprettelsesTidspunkt: new FormControl<Date | undefined>(undefined, {
        validators: [Validators.required],
      }),
    });

    effect(() => {
      const dokument = this.dokumenterService.selectedDokument();
      if (!dokument) {
        this.form.reset();
        this.form.patchValue({
          oprettelsesTidspunkt: dayjs().toDate(),
        });
      } else {
        this.form.setValue({
          emne: dokument.emne || '',
          oprettelsesTidspunkt: dokument.oprettelsesTidspunkt || '',
          indhold: dokument.indhold || '',
        });
      }
    });
  }

  async onClickSave() {
    await this.saveChanges();
  }

  async onClickDelete(event: Event) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Er du sikker?',
      header: 'Slet opslag',
      rejectButtonProps: {
        label: 'Fortryd',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Slet',
        severity: 'danger',
      },

      accept: async () => {
        const id = this.dokumenterService.selectedDokument()?.id;
        if (id) {
          await this.datasource.remove(id);
        }
        this.dokumenterService.activeTab.set('list');
      },
    });
  }

  async saveChanges() {
    if (this.form.invalid) {
      return;
    }

    this.saving.set(true);

    const dokument = this.dokumenterService.selectedDokument();

    const formData = new FormData();
    Object.keys(this.form.controls).forEach(formControlName => {
      formData.append(formControlName, this.form.get(formControlName)?.value);
    });

    if (dokument?.id) {
      await this.datasource.updateMultipart(dokument.id, formData);
    } else {
      await this.datasource.createMultipart(formData);
    }

    this.saving.set(false);
    this.dokumenterService.activeTab.set('list');
  }

  onFileSelect(event: FileSelectEvent) {
    if (event.currentFiles && event.currentFiles.length > 0) {
      const file = event.currentFiles[0];
      this.form.patchValue({ indhold: file });
    }
  }

  protected readonly appConfig = appConfig;
}
